/**
 * Web push oddany Segmentify — jedno źródło prawdy dla trzech niezależnych warstw.
 *
 * Decyzja (2026-09-16): na youlook kanał web push przejmuje Segmentify (kampania
 * PUSH_PERMISSION), a własny push VAPID (`/api/store/push/*` + crony
 * push-cart-abandoned / push-blast-dispatcher / push-cleanup w inventory) jest tam
 * WYŁĄCZANY. Powód techniczny: jeden scope service workera (`/`) = JEDNA rejestracja
 * = JEDNA subskrypcja `pushManager` z JEDNYM `applicationServerKey`. Dwóch nadawców
 * się tam nie mieści — drugi dostaje `InvalidStateError` przy `subscribe()`.
 *
 * Warstwy czytające tę stałą:
 *  1. Service worker — wtyczka builda (`app/sw/segmentifyPushPrelude.ts`) dokleja
 *     `importScripts` SW-a Segmentify jako pierwszą linię `sw.js`, a `app/sw/sw.ts`
 *     NIE rejestruje własnych handlerów `push`/`notificationclick`. SW nie widzi
 *     `runtimeConfig`, więc rozpoznaje sklep po {@link SEGMENTIFY_PUSH_DOMAINS}.
 *  2. Klient (`app/composables/usePwa.ts`, `app/plugins/pwa-register.client.ts`) —
 *     nie pokazuje własnego soft-aska, nie subskrybuje, a ZASTANĄ subskrypcję
 *     sprząta TYLKO, gdy jest nasza ({@link isOwnPushSubscription}), żeby Segmentify
 *     miał wolne miejsce na swoją — i żeby jej potem nie skasować.
 *  3. Nitro (`server/routes/manifest.json.get.ts`) — wystawia `/manifest.json`
 *     wymagany przez Segmentify tylko na tych sklepach.
 *
 * Warstwy 1 i 3 używają różnych kluczy (host vs brand) z konieczności — SW zna tylko
 * `self.location.hostname`, Nitro zna `NUXT_PUBLIC_BRAND`. Trzymamy oba tutaj, żeby
 * dopisanie kolejnego sklepu było jedną zmianą w jednym pliku.
 */

/**
 * Domeny bazowe sklepów, na których push należy do Segmentify. Dopasowanie jest
 * sufiksowe ({@link segmentifyOwnsPushOnHost}), więc obejmuje `www.` oraz staging
 * (`kykdnfsh.youlook.pl`) bez wypisywania każdego hosta z osobna.
 */
export const SEGMENTIFY_PUSH_DOMAINS = ['youlook.pl'] as const

/** Brandy (`NUXT_PUBLIC_BRAND`) odpowiadające {@link SEGMENTIFY_PUSH_DOMAINS}. */
export const SEGMENTIFY_PUSH_BRANDS = new Set<string>(['youlook'])

/**
 * Service worker Segmentify. Nie hostujemy jego kopii — `importScripts` ładuje
 * zawsze aktualną wersję z ich CDN-u, dokładnie jak w instrukcji integracji
 * (ich `sw.js` do wgrania w root to jedna linia `importScripts` tego adresu).
 */
export const SEGMENTIFY_PUSH_SW_URL = 'https://cdn.segmentify.com/v3/push/sw.js'

/**
 * Treść `/manifest.json` wymaganego przez integrację Segmentify — 1:1 z pliku,
 * który dostarczyli. To NIE jest manifest PWA sklepu (ten żyje pod
 * `/site.webmanifest` i jest budowany per-brand w `app/brands/manifest.ts`);
 * `gcm_sender_id` to uniwersalny legacy-FCM sender Segmentify, potrzebny tylko
 * przeglądarkom sprzed VAPID.
 */
export const SEGMENTIFY_PUSH_MANIFEST = {
  name: 'Push Notifications',
  start_url: '/',
  gcm_sender_id: '103953800507',
} as const

/**
 * Klucz publiczny VAPID NASZEGO kanału push (jawny — to `applicationServerKey`
 * z przeglądarki), którym youlook zakładał subskrypcje do 2026-09-16. W runtime
 * configu youlooka jest już pusty (`nuxt_public_pwa_vapid_public_key: ""`), więc
 * klient bierze go stąd — tylko po to, żeby odróżnić NASZĄ zastaną subskrypcję
 * od subskrypcji Segmentify (obie siedzą w tej samej rejestracji SW).
 */
export const OWN_PUSH_LEGACY_VAPID_PUBLIC_KEY
  = 'BB3ybgoooOkT4ECH23lVpFC-gHNEFE43fUkqwvXQjYTfqbsocbhLVg37zOUhRQK8nhEQ5HP2rA0JHV202JbDGOk'

/**
 * Czy subskrypcję założyliśmy MY — `applicationServerKey` równy bajt w bajt naszemu
 * kluczowi VAPID. Brak klucza (`null` — np. legacy `gcm_sender_id`) = nie nasza.
 */
export function isOwnPushSubscription(
  applicationServerKey: ArrayBuffer | null | undefined,
  vapidPublicKey: string = OWN_PUSH_LEGACY_VAPID_PUBLIC_KEY,
): boolean {
  if (!applicationServerKey) return false
  const actual = new Uint8Array(applicationServerKey)
  const base64 = vapidPublicKey.replace(/-/g, '+').replace(/_/g, '/')
  const raw = atob(base64 + '='.repeat((4 - (base64.length % 4)) % 4))
  if (actual.length !== raw.length) return false
  for (let i = 0; i < raw.length; i++) {
    if (actual[i] !== raw.charCodeAt(i)) return false
  }
  return true
}

/** Czy na tym hoście push obsługuje Segmentify (dopasowanie sufiksowe domeny). */
export function segmentifyOwnsPushOnHost(hostname: string): boolean {
  return SEGMENTIFY_PUSH_DOMAINS.some(
    domain => hostname === domain || hostname.endsWith(`.${domain}`),
  )
}

/** Czy na tym brandzie push obsługuje Segmentify. */
export function segmentifyOwnsPushOnBrand(brand: string | undefined | null): boolean {
  return !!brand && SEGMENTIFY_PUSH_BRANDS.has(brand)
}
